import { useCallback, useEffect } from 'react'

export type GTMActionType = 'click' | 'create' | 'save' | 'edit' | 'delete' | 'toggle'

/**
 * Hook for sending triggers to Google Tag Manager (GTM)
 *
 * @returns Object containing the sendGTMTrigger function
 *
 */
export const useGTM = () => {
  // Initialize dataLayer if it doesn't exist
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
    }
  }, [])

  /**
   * Sends a trigger to GTM dataLayer
   *
   * @param eventName - The name of the event
   * @param eventAction - Optional action associated with the event
   * @param eventPayload - Optional additional data to send with the event
   */
  const sendGTMTrigger = useCallback(
    (eventName: string, eventAction: GTMActionType, eventPayload?: Record<string, any>) => {
      if (typeof window === 'undefined') {
        console.warn('GTM: window is not available.')
        return
      }

      // Initialize dataLayer if it doesn't exist
      if (!window.dataLayer) {
        window.dataLayer = []
      }

      // Build the event object
      const eventData: Record<string, any> = {
        event: eventName,
        action: eventAction,
        ...(eventPayload && eventPayload)
      }

      // Push to dataLayer
      try {
        window.dataLayer.push(eventData)
      } catch (error) {
        console.error('GTM: Error sending trigger:', error)
      }
    },
    []
  )

  return {
    sendGTMTrigger
  }
}

export default useGTM
